import React from 'react';
import PropTypes from 'prop-types';
import { useMobile } from '../../hooks/useMobile';
import {
  validateRowerSideForSeat,
  getExpectedSideForSeat,
  parseRiggingConfig,
} from '../../utils/riggingValidation';

const SeatSlot = ({
  seat_number,
  rowerInSeat,
  isDropTarget,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onRemoveRower,
  onDragStart, // Add drag start handler for seated rowers
  // Mobile-specific props
  availableRowers = [],
  onMobileAssign,
  onMobileChangeSeat,
  crew,
}) => {
  const { isMobile } = useMobile();

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling to parent container
    onDrop(e, seat_number);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    onDragEnter(e, seat_number);
  };

  const handleRemove = () => {
    if (rowerInSeat) {
      onRemoveRower(rowerInSeat.id);
    }
  };

  const handleRowerDragStart = (e) => {
    if (rowerInSeat && onDragStart) {
      // Pass the rower's user data for dragging
      onDragStart(e, rowerInSeat.user);
    }
  };

  // Mobile dropdown handlers
  const handleMobileRowerSelect = (e) => {
    const selectedRowerId = e.target.value;
    if (selectedRowerId && onMobileAssign) {
      onMobileAssign(parseInt(selectedRowerId), seat_number);
    }
  };

  const handleMobileSeatChange = (e) => {
    const newSeatNumber = e.target.value;
    if (rowerInSeat && onMobileChangeSeat) {
      if (newSeatNumber === 'remove') {
        onRemoveRower(rowerInSeat.id);
      } else {
        onMobileChangeSeat(rowerInSeat.user.id, parseInt(newSeatNumber));
      }
    }
  };

  // Determine seat display info
  const getSeatInfo = () => {
    if (seat_number === 0) {
      return { seatNumber: 'Cox', displayText: 'Coxswain' };
    }

    if (seat_number === 1) {
      return { seatNumber: '1', displayText: 'Bow (Seat 1)' };
    }

    // For other seats, just show the seat number
    return { seatNumber: seat_number.toString(), displayText: `Seat ${seat_number}` };
  };

  const seatInfo = getSeatInfo();

  // Filter available rowers to exclude already assigned ones
  const getAvailableRowersForDropdown = () => {
    if (!availableRowers) return [];

    return availableRowers.filter((rower) => {
      // Don't show the current rower in their own seat
      if (rowerInSeat && rower.id === rowerInSeat.user?.id) return false;
      return true;
    });
  };

  const availableForDropdown = getAvailableRowersForDropdown();

  // Check for side preference validation
  const hasSideMismatch = () => {
    if (
      !crew ||
      crew.is_experimental ||
      !crew.side_preference_strict ||
      !rowerInSeat ||
      seat_number === 0
    ) {
      return false;
    }

    const riggingConfig = parseRiggingConfig(crew.rigging_config);
    const expectedSide = getExpectedSideForSeat(riggingConfig, seat_number);

    if (!expectedSide) return false;

    const validation = validateRowerSideForSeat(rowerInSeat.user, expectedSide);

    return !validation.isValid;
  };

  // Get expected side for this seat from rigging configuration
  const getExpectedSideForSeatFromRigging = () => {
    if (!crew || crew.is_experimental || seat_number === 0) return null;

    const riggingConfig = parseRiggingConfig(crew.rigging_config);
    return getExpectedSideForSeat(riggingConfig, seat_number);
  };

  // Get rower's preferred side
  const getRowerPreferredSide = () => {
    if (!rowerInSeat) return null;

    return rowerInSeat.user?.preferred_side;
  };

  const sideMismatch = hasSideMismatch();
  const expectedSide = getExpectedSideForSeatFromRigging();
  const rowerPreferredSide = getRowerPreferredSide();

  // Get side preference indicator styling
  const getSideIndicatorStyle = (side) => {
    switch (side) {
      case 'port':
        return 'bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs font-medium';
      case 'starboard':
        return 'bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-medium';
      case 'both':
        return 'bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-medium';
      case 'none':
        return 'bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full text-xs font-medium';
      default:
        return 'bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full text-xs font-medium';
    }
  };

  return (
    <div
      className={`relative rounded-lg border-2 border-dashed p-4 transition-all duration-200 ${
        isDropTarget
          ? 'scale-105 border-blue-500 bg-blue-50'
          : rowerInSeat
            ? sideMismatch
              ? 'border-amber-300 bg-amber-50'
              : 'border-green-300 bg-green-50'
            : 'border-gray-300 hover:border-gray-400'
      }`}
      onDragOver={!isMobile ? onDragOver : undefined}
      onDragEnter={!isMobile ? handleDragEnter : undefined}
      onDragLeave={!isMobile ? onDragLeave : undefined}
      onDrop={!isMobile ? handleDrop : undefined}
    >
      <div className="mb-2 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <div className="text-xs text-gray-500">{seatInfo.displayText}</div>
            {/* Expected side indicator for the seat */}
            {expectedSide && (
              <span
                className={`${getSideIndicatorStyle(expectedSide)}`}
                title={`Expected: ${expectedSide} side`}
              >
                {expectedSide}
              </span>
            )}
          </div>
          {sideMismatch && (
            <div className="text-xs font-medium text-amber-600">⚠️ Side mismatch</div>
          )}
        </div>
        {rowerInSeat && !isMobile && (
          <button
            onClick={handleRemove}
            className="flex h-6 w-6 items-center justify-center rounded-full text-lg font-bold text-gray-400 transition-colors hover:text-red-500"
            title="Remove from seat"
          >
            ×
          </button>
        )}
      </div>

      {/* Desktop: Drag and Drop Interface */}
      {!isMobile && (
        <>
          {rowerInSeat ? (
            <div
              className="cursor-grab rounded-md bg-white p-3 shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing"
              draggable={!!onDragStart}
              onDragStart={handleRowerDragStart}
              title="Drag to move to another seat"
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900">
                    {rowerInSeat.user?.first_name} {rowerInSeat.user?.last_name}
                  </div>
                  <div className="text-sm text-gray-600">{rowerInSeat.user?.email}</div>
                </div>
                {/* Rower's preferred side indicator */}
                {rowerPreferredSide && (
                  <div className="ml-2 flex items-center gap-1">
                    <span
                      className={`${getSideIndicatorStyle(rowerPreferredSide)}`}
                      title={`Prefers ${rowerPreferredSide === 'none' ? 'no specific' : rowerPreferredSide} side`}
                    >
                      {rowerPreferredSide === 'none' ? 'No Preference' : rowerPreferredSide}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex h-16 items-center justify-center text-gray-400">
              <div className="text-center">
                <div className="text-sm">Drop rower here</div>
                {isDropTarget && <div className="mt-1 text-xs">Release to assign</div>}
              </div>
            </div>
          )}
        </>
      )}

      {/* Mobile: Dropdown Interface */}
      {isMobile && (
        <>
          {rowerInSeat ? (
            <div className="space-y-3">
              {/* Current rower display */}
              <div className="rounded-md bg-white p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900">
                      {rowerInSeat.user?.first_name} {rowerInSeat.user?.last_name}
                    </div>
                    <div className="text-sm text-gray-600">{rowerInSeat.user?.email}</div>
                  </div>
                  {/* Rower's preferred side indicator */}
                  {rowerPreferredSide && (
                    <div className="ml-2 flex items-center gap-1">
                      <span
                        className={`${getSideIndicatorStyle(rowerPreferredSide)}`}
                        title={`Prefers ${rowerPreferredSide === 'none' ? 'no specific' : rowerPreferredSide} side`}
                      >
                        {rowerPreferredSide === 'none' ? 'No Preference' : rowerPreferredSide}
                      </span>
                      {/* Note: Users don't have strict side preferences, only crews do */}
                    </div>
                  )}
                </div>
              </div>

              {/* Change seat dropdown */}
              {onMobileChangeSeat && (
                <select
                  onChange={handleMobileSeatChange}
                  value=""
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Change seat...</option>
                  <option value="remove">Remove from crew</option>
                  {/* You might want to add other seat options here based on boat type */}
                </select>
              )}
            </div>
          ) : /* Empty seat with dropdown to assign rower */
          availableForDropdown.length > 0 && onMobileAssign ? (
            <div className="space-y-2">
              <div className="text-center text-gray-400">
                <div className="text-sm">Select rower for this seat</div>
              </div>
              <select
                onChange={handleMobileRowerSelect}
                value=""
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Choose rower...</option>
                {availableForDropdown.map((rower) => (
                  <option key={rower.id} value={rower.id}>
                    {rower.first_name} {rower.last_name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="flex h-16 items-center justify-center text-gray-400">
              <div className="text-center">
                <div className="text-sm">No rowers available</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

SeatSlot.propTypes = {
  seat_number: PropTypes.number.isRequired, // Can be 0 for cox
  rowerInSeat: PropTypes.object,
  isDropTarget: PropTypes.bool,
  onDragOver: PropTypes.func,
  onDragEnter: PropTypes.func,
  onDragLeave: PropTypes.func,
  onDrop: PropTypes.func,
  onRemoveRower: PropTypes.func,
  onDragStart: PropTypes.func, // Add onDragStart to PropTypes
  // Mobile-specific props
  availableRowers: PropTypes.array,
  onMobileAssign: PropTypes.func,
  onMobileChangeSeat: PropTypes.func,
  crew: PropTypes.object, // Crew object for validation
};

export default SeatSlot;
